import React, { Fragment, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import PointsPaySlider, { usePointsPaySlider } from 'components/PointsPaySlider';
import Currency from 'components/Currency';
import { Box, Flex, Text } from 'roo-ui/components';
import { hashObject } from 'lib/hash';
import OfferCheckoutLink from './OfferCheckoutLink';
import OfferPayableAtProperty from './OfferPayableAtProperty';
import { PAYMENT_METHODS } from 'lib/enums/payment';
import { getQueryPayWith } from 'store/search/searchSelectors/querySelectors';

const OfferPointsPaySlider = ({ totalCash, payableAtProperty, propertyId, offerId, roomTypeId, offerName, onCashPaymentAmountChange }) => {
  const { pointsAmount, cashAmount, minCashAmount, maxCashAmount, initialCashAmount, onChange } = usePointsPaySlider({
    totalCash,
    payableAtProperty,
  });

  const queryPayWith = useSelector(getQueryPayWith);
  const payWith = pointsAmount.greaterThan(0) ? PAYMENT_METHODS.POINTS : queryPayWith;
  const pointsPaySliderKey = hashObject({ minCashAmount, maxCashAmount, initialCashAmount });

  useEffect(() => {
    onCashPaymentAmountChange({ cashAmount });
  }, [cashAmount.toNumber(), onCashPaymentAmountChange]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Fragment>
      <Box my={[6, 1]} data-testid="points-pay-wrapper">
        <Flex justifyContent="flex-end" mb={2}>
          <Box>
            <Currency amount={pointsAmount} currency="PTS" fontSize={[32, 24, 32]} data-testid="points-amount" />
          </Box>
          <Box px={2}>
            <Text fontSize={['3xl', 'xl', '3xl']} lineHeight="tight">
              +
            </Text>
          </Box>
          <Box>
            <Currency amount={cashAmount} currency="AUD" fontSize={[32, 24, 32]} hideCurrency data-testid="cash-amount" />
          </Box>
        </Flex>
        <PointsPaySlider
          key={pointsPaySliderKey}
          minCashAmount={minCashAmount.toNumber()}
          maxCashAmount={maxCashAmount.toNumber()}
          initialCashAmount={initialCashAmount.toNumber()}
          onChange={onChange}
        />
        <Flex justifyContent="space-between" mt={2} color="greys.dusty">
          <Box>Points</Box>
          <Box>Cash (AUD)</Box>
        </Flex>
      </Box>
      <OfferPayableAtProperty payableAtProperty={payableAtProperty} isIncludedInTotal={true} />
      <OfferCheckoutLink
        propertyId={propertyId}
        roomTypeId={roomTypeId}
        offerId={offerId}
        initialCash={cashAmount.toNumber()}
        offerName={offerName}
        payWith={payWith}
      />
    </Fragment>
  );
};

OfferPointsPaySlider.propTypes = {
  totalCash: PropTypes.shape({
    amount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    currency: PropTypes.string.isRequired,
  }).isRequired,
  payableAtProperty: PropTypes.shape({
    amount: PropTypes.string.isRequired,
  }).isRequired,
  propertyId: PropTypes.string.isRequired,
  roomTypeId: PropTypes.string.isRequired,
  offerId: PropTypes.string.isRequired,
  offerName: PropTypes.string.isRequired,
  onCashPaymentAmountChange: PropTypes.func.isRequired,
};

export default OfferPointsPaySlider;
